import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Send, X, Zap } from "lucide-react";
import { grantXP, saveProgress } from "@/components/medusa/player";

/* ================= BEAT MEMORY (progressive) ================= */
const MEM_PADS = [
  { id: 0, label: "Kick", color: "#FF3399", freq: 90 },
  { id: 1, label: "808", color: "#00F0FF", freq: 65 },
  { id: 2, label: "Snare", color: "#A855F7", freq: 240 },
  { id: 3, label: "Hat", color: "#00FF66", freq: 880 },
];

export function BeatMemoryTrainer({ module, engine, cleared, onClose }) {
  const levelDef = module.levels[Math.min(cleared, module.levels.length - 1)];
  const [phase, setPhase] = useState("idle");
  const [seq, setSeq] = useState([]);
  const [round, setRound] = useState(0);
  const [lit, setLit] = useState(null);
  const [watching, setWatching] = useState(false);
  const [step, setStep] = useState(0);
  const [msg, setMsg] = useState("");
  const ROUNDS_TO_CLEAR = 5;

  const tone = (p, dur = 0.3) => {
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    const o = ctx.createOscillator();
    o.type = p.id === 1 ? "sine" : "square";
    o.frequency.value = p.freq;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.22, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + dur + 0.02);
  };

  const playSeq = useCallback(async (sequence) => {
    setWatching(true); setMsg("Watch…");
    for (const p of sequence) {
      await new Promise((r) => setTimeout(r, 520));
      setLit(p); tone(MEM_PADS[p]);
      await new Promise((r) => setTimeout(r, 280));
      setLit(null);
    }
    setWatching(false); setMsg("Your turn."); setStep(0);
  }, []);

  const start = () => {
    const first = Array.from({ length: levelDef.start_len }, () => Math.floor(Math.random() * 4));
    setSeq(first); setRound(0); setPhase("playing");
    playSeq(first);
  };

  const tap = async (id) => {
    if (watching || phase !== "playing") return;
    tone(MEM_PADS[id]);
    setLit(id); setTimeout(() => setLit(null), 200);
    if (id === seq[step]) {
      if (step + 1 >= seq.length) {
        const nextRound = round + 1;
        if (nextRound >= ROUNDS_TO_CLEAR) {
          setPhase("over");
          setMsg("");
          const xp = levelDef.xp;
          await grantXP("academy", xp, `Beat Memory level ${cleared + 1} cleared`, "beat-memory");
          await saveProgress("drill", "beat-memory", cleared + 1, 100);
        } else {
          setRound(nextRound);
          const next = [...seq, Math.floor(Math.random() * 4)];
          setSeq(next);
          setMsg(`Groove locked. Pattern grows to ${next.length}.`);
          setTimeout(() => playSeq(next), 900);
        }
      } else setStep(step + 1);
    } else {
      setMsg("The pattern broke — same level, fresh run.");
      setTimeout(() => setPhase("idle"), 700);
    }
  };

  return (
    <div className="rounded-2xl border border-emerald-500/25 bg-black/60 p-5 sm:p-6" data-testid="beat-memory-trainer">
      <TrainerHead title="Beat Memory" sub={`Level ${cleared + 1} · clear ${ROUNDS_TO_CLEAR} growing rounds · +${levelDef.xp} XP`} onClose={onClose} />
      {phase === "idle" && (
        <div className="text-center py-8">
          <p className="text-xs text-ice/55 max-w-xs mx-auto">Watch the pads glow, then repeat the groove. Five rounds cleared levels you up and raises the starting length.</p>
          <button onClick={start} className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-bold text-sm px-8 py-3.5 rounded-full active:scale-95 transition-all" data-testid="memory-trainer-start">
            <Play size={15} /> Start Level {cleared + 1}
          </button>
        </div>
      )}
      {phase !== "idle" && (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="memory-trainer-pads">
            {MEM_PADS.map((p) => (
              <button key={p.id} onClick={() => tap(p.id)} disabled={watching || phase === "over"}
                className="rounded-2xl border py-8 text-center transition-all duration-150 active:scale-95 disabled:cursor-wait"
                style={{
                  borderColor: lit === p.id ? p.color : "#26262c",
                  background: lit === p.id ? `${p.color}22` : "rgba(255,255,255,0.03)",
                  boxShadow: lit === p.id ? `0 0 26px ${p.color}66` : "none",
                }}
                data-testid={`memory-trainer-pad-${p.id}`}>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: lit === p.id ? p.color : "#71717a" }}>{p.label}</p>
              </button>
            ))}
          </div>
          <p className="mt-3 text-center text-xs font-mono text-emerald-300 h-4" data-testid="memory-trainer-msg">
            {phase === "over" ? `Level ${cleared + 1} CLEARED — +${levelDef.xp} XP` : `${msg} · Round ${round + 1}/${ROUNDS_TO_CLEAR} · length ${seq.length}`}
          </p>
        </>
      )}
    </div>
  );
}

/* ================= FIX THE MIX ================= */
function useMixEngine(engine) {
  const nodes = useRef(null);
  const timer = useRef(null);

  const start = (bands, comp) => {
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    stop();
    const lowG = ctx.createGain(); const midG = ctx.createGain(); const highG = ctx.createGain();
    lowG.gain.value = bands.low / 66; midG.gain.value = bands.mid / 66; highG.gain.value = bands.high / 66;
    const compNode = ctx.createDynamicsCompressor();
    compNode.threshold.value = -6 - (comp / 100) * 34;
    compNode.ratio.value = 1 + (comp / 100) * 11;
    lowG.connect(compNode); midG.connect(compNode); highG.connect(compNode);
    compNode.connect(ctx.destination);
    const bpm = 100; const beat = 60 / bpm;
    let step = 0;
    let nextT = ctx.currentTime + 0.06;
    const kick = (t) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.frequency.setValueAtTime(140, t); o.frequency.exponentialRampToValueAtTime(42, t + 0.12); g.gain.setValueAtTime(0.9, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.3); o.connect(g); g.connect(lowG); o.start(t); o.stop(t + 0.32); };
    const bass = (t, f) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; const flt = ctx.createBiquadFilter(); flt.type = "lowpass"; flt.frequency.value = 300; g.gain.setValueAtTime(0.3, t); g.gain.exponentialRampToValueAtTime(0.001, t + beat * 0.9); o.connect(flt); flt.connect(g); g.connect(lowG); o.start(t); o.stop(t + beat); };
    const pluck = (t, f) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.2, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.35); o.connect(g); g.connect(midG); o.start(t); o.stop(t + 0.4); };
    const hat = (t) => { const b = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate); const d = b.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length); const s = ctx.createBufferSource(); s.buffer = b; const f = ctx.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 7000; const g = ctx.createGain(); g.gain.value = 0.16; s.connect(f); f.connect(g); g.connect(highG); s.start(t); };
    const mel = [220, 261.63, 329.63, 261.63];
    const tick = () => {
      while (nextT < ctx.currentTime + 0.15) {
        const s = step % 16;
        if (s % 4 === 0) kick(nextT);
        if (s % 8 === 4) bass(nextT, s === 4 ? 55 : 65.41);
        if (s % 2 === 0) hat(nextT);
        if (s % 4 === 2) pluck(nextT, mel[(step / 4) % 4 | 0]);
        step += 1; nextT += beat / 2;
      }
      timer.current = setTimeout(tick, 50);
    };
    tick();
    nodes.current = { lowG, midG, highG, compNode };
  };

  const update = (bands, comp) => {
    const n = nodes.current;
    if (!n) return;
    const t = engine.current.ctx.currentTime;
    n.lowG.gain.setTargetAtTime(bands.low / 66, t, 0.03);
    n.midG.gain.setTargetAtTime(bands.mid / 66, t, 0.03);
    n.highG.gain.setTargetAtTime(bands.high / 66, t, 0.03);
    n.compNode.threshold.setTargetAtTime(-6 - (comp / 100) * 34, t, 0.03);
    n.compNode.ratio.setTargetAtTime(1 + (comp / 100) * 11, t, 0.03);
  };

  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (nodes.current) { try { nodes.current.compNode.disconnect(); } catch { /* noop */ } nodes.current = null; }
  };

  useEffect(() => stop, []);
  return { start, update, stop };
}

export function FixTheMixTrainer({ module, engine, onClose }) {
  const [scenario, setScenario] = useState(null);
  const [bands, setBands] = useState({ low: 50, mid: 50, high: 50 });
  const [comp, setComp] = useState(30);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);
  const mix = useMixEngine(engine);

  const flawedStart = (sc) => {
    // start audibly wrong: offsets away from target
    const off = { low: Math.min(100, Math.max(5, sc.target.low + 30)), mid: Math.min(100, Math.max(5, sc.target.mid - 25)), high: Math.min(100, Math.max(5, sc.target.high - 30)) };
    const offComp = Math.max(5, sc.target.comp - 25);
    setBands(off); setComp(offComp);
    setScenario(sc); setResult(null); setPlaying(true);
    mix.start(off, offComp);
  };

  useEffect(() => { if (playing) mix.update(bands, comp); }, [bands, comp]); // eslint-disable-line

  const submit = async () => {
    const t = scenario.target;
    const dev = (Math.abs(bands.low - t.low) + Math.abs(bands.mid - t.mid) + Math.abs(bands.high - t.high) + Math.abs(comp - t.comp)) / 4;
    const within = dev <= scenario.tolerance;
    const score = Math.max(0, Math.round(100 - dev * (100 / (scenario.tolerance * 3))));
    mix.stop(); setPlaying(false);
    setResult({ score, within });
    if (within) {
      await grantXP("academy", scenario.xp, `Fix the Mix: ${scenario.title} repaired`, scenario.scenario_id);
      await saveProgress("drill", scenario.scenario_id, score, score);
    }
  };

  const stopAll = () => { mix.stop(); setPlaying(false); };

  return (
    <div className="rounded-2xl border border-teal-500/25 bg-black/60 p-5 sm:p-6" data-testid="fix-the-mix-trainer">
      <TrainerHead title="Fix the Mix" sub={scenario ? scenario.title : "Pick the flawed session"} onClose={() => { stopAll(); onClose(); }} />
      {!scenario ? (
        <div className="space-y-2.5" data-testid="mix-scenarios">
          {module.scenarios.map((sc) => (
            <button key={sc.scenario_id} onClick={() => flawedStart(sc)}
              className="w-full text-left rounded-xl border border-teal-500/25 bg-[#0B0B10]/80 p-4 hover:border-teal-400/60 transition-all active:scale-[0.99]"
              data-testid={`mix-scenario-${sc.scenario_id}`}>
              <p className="font-display text-white text-sm font-bold">{sc.title} <span className="font-mono text-[9px] text-yellow-300/80 ml-2">+{sc.xp} XP</span></p>
              <p className="text-xs text-ice/50 mt-0.5">{sc.flaw}</p>
            </button>
          ))}
        </div>
      ) : (
        <>
          <p className="text-xs text-ice/55 mb-4">{scenario.flaw} Repair it with the console — your ears against the reference.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="mix-console">
            {[["low", "Low End"], ["mid", "Mids"], ["high", "Highs"]].map(([k, label]) => (
              <div key={k} className="rounded-xl border border-[#26262e] bg-[#0c0c10] p-3">
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-teal-300 mb-2">{label}</p>
                <input type="range" min="0" max="100" value={bands[k]} onChange={(e) => setBands((b) => ({ ...b, [k]: Number(e.target.value) }))}
                  className="w-full accent-teal-400" data-testid={`mix-${k}`} />
                <p className="text-center font-mono text-xs text-white mt-1" data-testid={`mix-${k}-val`}>{bands[k]}</p>
              </div>
            ))}
            <div className="rounded-xl border border-[#26262e] bg-[#0c0c10] p-3">
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber-300 mb-2">Glue Comp</p>
              <input type="range" min="0" max="100" value={comp} onChange={(e) => setComp(Number(e.target.value))}
                className="w-full accent-amber-400" data-testid="mix-comp" />
              <p className="text-center font-mono text-xs text-white mt-1" data-testid="mix-comp-val">{comp}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => { if (playing) { stopAll(); } else { setPlaying(true); mix.start(bands, comp); } }}
              className="inline-flex items-center gap-2 border border-teal-500/50 text-teal-300 font-bold text-xs px-5 py-2.5 rounded-full hover:bg-teal-500/10 transition-all"
              data-testid="mix-play-toggle">
              <Play size={13} /> {playing ? "Stop" : "Hear It"}
            </button>
            <button onClick={submit} className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-black font-bold text-xs px-6 py-2.5 rounded-full active:scale-95 transition-all" data-testid="mix-submit">
              <Send size={13} /> Print the Mix
            </button>
          </div>
          {result && (
            <div className={`mt-4 rounded-xl border p-4 text-center ${result.within ? "border-emerald-400/40 bg-emerald-950/20" : "border-red-500/40 bg-red-950/15"}`} data-testid="mix-result">
              <p className="font-display text-white font-bold">{result.within ? `Repaired — reference match ${result.score}% · +${scenario.xp} XP` : `Off reference — ${result.score}%. Listen again and rebalance.`}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================= 808 MASTERY ================= */
export function Mastery808Trainer({ module, engine, onClose }) {
  const [drill, setDrill] = useState(null);
  const [freq, setFreq] = useState(80);
  const [msg, setMsg] = useState("");
  const [result, setResult] = useState(null);
  const hitsRef = useRef([]);
  const timesRef = useRef([]);

  const play808 = (f) => {
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.6, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + 0.75);
  };

  const submitTune = async () => {
    const target = drill.target_hz;
    const diff = Math.abs(freq - target);
    const passed = diff <= 3;
    setResult(passed ? { ok: true, text: `Locked at ${freq} Hz — within ${diff.toFixed(1)} Hz of the key. +${drill.xp} XP` } : { ok: false, text: `${diff.toFixed(1)} Hz off the key. Sweep slower — feel where it stops beating against the reference.` });
    if (passed) {
      await grantXP("academy", drill.xp, `808 Mastery: ${drill.title}`, drill.drill_id);
      await saveProgress("drill", drill.drill_id, Math.round(100 - diff * 10), 100);
    }
  };

  const runGrid = () => {
    // 4 kicks at 100bpm after a 2-beat count-in; player taps on each kick
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    const beat = 0.6;
    const t0 = ctx.currentTime + beat * 2;
    timesRef.current = [0, 1, 2, 3].map((i) => t0 + i * beat);
    hitsRef.current = [];
    setResult(null); setMsg("Count-in… drop the 808 exactly on each kick.");
    [0, 1, 2, 3].forEach((i) => {
      const t = t0 + i * beat;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.setValueAtTime(150, t); o.frequency.exponentialRampToValueAtTime(45, t + 0.1);
      g.gain.setValueAtTime(0.8, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t + 0.3);
    });
    setTimeout(async () => {
      const errs = timesRef.current.map((t, i) => (hitsRef.current[i] != null ? Math.abs(hitsRef.current[i] - t) * 1000 : 999));
      const avg = errs.reduce((a, b) => a + b, 0) / errs.length;
      const passed = avg <= 120;
      setResult(passed
        ? { ok: true, text: `Grid locked — average ${Math.round(avg)} ms off the kick. +${drill.xp} XP` }
        : { ok: false, text: `Average ${Math.round(avg)} ms off. The 808 lives WITH the kick, not after it.` });
      setMsg("");
      if (passed) {
        await grantXP("academy", drill.xp, `808 Mastery: ${drill.title}`, drill.drill_id);
        await saveProgress("drill", drill.drill_id, Math.max(0, Math.round(100 - avg / 3)), 100);
      }
    }, (2 + 4) * 600 + 600);
  };

  const dropHit = () => {
    const ctx = engine?.current?.ctx;
    if (!ctx || !timesRef.current.length) return;
    play808(freq);
    const now = ctx.currentTime;
    const idx = hitsRef.current.length;
    if (idx < 4) hitsRef.current.push(now);
  };

  return (
    <div className="rounded-2xl border border-amber-500/25 bg-black/60 p-5 sm:p-6" data-testid="mastery-808-trainer">
      <TrainerHead title="808 Mastery" sub={drill ? drill.title : "Choose the drill"} onClose={onClose} />
      {!drill ? (
        <div className="space-y-2.5" data-testid="808-drills">
          {module.drills.map((d) => (
            <button key={d.drill_id} onClick={() => { setDrill(d); setResult(null); setMsg(""); }}
              className="w-full text-left rounded-xl border border-amber-500/25 bg-[#0B0B10]/80 p-4 hover:border-amber-400/60 transition-all active:scale-[0.99]"
              data-testid={`drill-${d.drill_id}`}>
              <p className="font-display text-white text-sm font-bold">{d.title} <span className="font-mono text-[9px] text-yellow-300/80 ml-2">+{d.xp} XP</span></p>
              <p className="text-xs text-ice/50 mt-0.5">{d.task}</p>
            </button>
          ))}
        </div>
      ) : (
        <>
          <p className="text-xs text-ice/55 mb-4">{drill.task}</p>
          <div className="rounded-xl border border-[#26262e] bg-[#0c0c10] p-4 mb-4">
            <div className="flex justify-between mb-1.5">
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber-300">808 Tuning</span>
              <span className="font-mono text-xs text-white" data-testid="freq-val">{freq.toFixed(1)} Hz</span>
            </div>
            <input type="range" min="40" max="160" step="0.1" value={freq} onChange={(e) => setFreq(Number(e.target.value))} className="w-full accent-amber-400" data-testid="freq-slider" />
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => play808(drill.target_hz)} className="inline-flex items-center gap-2 border border-white/20 text-ice/70 font-bold text-xs px-5 py-2.5 rounded-full hover:border-amber-400/60 hover:text-amber-300 transition-all" data-testid="ref-tone">
              Reference
            </button>
            <button onClick={() => play808(freq)} className="inline-flex items-center gap-2 border border-amber-500/50 text-amber-300 font-bold text-xs px-5 py-2.5 rounded-full hover:bg-amber-500/10 transition-all" data-testid="my-808">
              <Zap size={13} /> My 808
            </button>
            {drill.hits ? (
              <button onClick={runGrid} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold text-xs px-6 py-2.5 rounded-full active:scale-95 transition-all" data-testid="grid-start">
                Run the Grid
              </button>
            ) : (
              <button onClick={submitTune} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold text-xs px-6 py-2.5 rounded-full active:scale-95 transition-all" data-testid="tune-submit">
                Lock Tuning
              </button>
            )}
            {drill.hits && timesRef.current.length > 0 && !result && (
              <button onClick={dropHit} className="inline-flex items-center gap-2 bg-amber-400 text-black font-bold text-sm px-8 py-2.5 rounded-full active:scale-90 transition-all" data-testid="grid-drop">
                DROP 808
              </button>
            )}
          </div>
          {msg && <p className="mt-3 text-xs font-mono text-amber-200" data-testid="808-msg">{msg}</p>}
          {result && (
            <div className={`mt-4 rounded-xl border p-4 text-center ${result.ok ? "border-emerald-400/40 bg-emerald-950/20" : "border-red-500/40 bg-red-950/15"}`} data-testid="808-result">
              <p className="font-display text-white text-sm font-bold">{result.text}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const TrainerHead = ({ title, sub, onClose }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice/45">{sub}</p>
      <p className="font-display text-white font-bold">{title}</p>
    </div>
    <button onClick={onClose} aria-label="Close trainer" className="text-ice/40 hover:text-white transition-colors" data-testid="trainer-close"><X size={18} /></button>
  </div>
);
